import { useEffect } from "react";
import { createPortal } from "react-dom";

/**
 * Right-hand panel for workspace edits. Rendered on the document body so the
 * app sidebar does not clip it.
 * @param {{
 *   open: boolean,
 *   title: string,
 *   onClose: () => void,
 *   children: React.ReactNode,
 *   footer?: React.ReactNode,
 * }} props
 * @returns {JSX.Element|null}
 */
export function WorkspaceDrawer({ open, title, onClose, children, footer }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className="job-workspace-overlay job-workspace-v2 fixed inset-0 z-[70] flex justify-end">
      <button
        type="button"
        aria-label="Close panel"
        className="absolute inset-0 bg-[#202b33]/20"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative flex h-full w-full max-w-[440px] flex-col bg-white shadow-xl"
      >
        <div className="flex items-center justify-between gap-3 border-b border-[#e2e7ea] px-5 py-4">
          <h2 className="text-[16px] font-semibold leading-normal text-[#202b33]">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-[12px] font-semibold leading-normal text-[#6e7b84] hover:text-[#202b33]"
          >
            Close
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>
        {footer ? (
          <div className="flex items-center justify-end gap-2 border-t border-[#e2e7ea] px-5 py-4">
            {footer}
          </div>
        ) : null}
      </aside>
    </div>,
    document.body
  );
}
